import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDownloadSewExports } from '@/backoffice/features/data-exports/hooks/use-download-sew-exports/use-download-sew-exports';
import { SewBillingCycleRangeExportForm } from '@/backoffice/features/data-exports/sew-billing-cycle-range-export/sew-billing-cycle-range-export-form';
import {
  TSewBillingCycleRangeExportInputSchema,
  TSewBillingCycleRangeExportProps,
} from '@/backoffice/features/data-exports/sew-billing-cycle-range-export/sew-billing-cycle-range-export.types';
import { sewBillingCycleRangeExportValidationInputSchema } from '@/backoffice/features/data-exports/sew-billing-cycle-range-export/sew-billing-cycle-range-export.validation';
import { getLatestExportableBillingCycle } from '@/backoffice/features/data-exports/utils/latest-exportable-billing-cycle';
import { ButtonWrapper } from '@/shared/components/ui/button/button-wrapper/button-wrapper';
import { FeatureCard } from '@/shared/components/ui/feature-card/feature-card';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { ModalController } from '@/shared/components/ui/modal-controller/modal-controller';
import { ModalContainer } from '@/shared/components/ui/modal-controller/modal-controller.styled';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { isoDateToBillingTs } from '@/shared/utils/format';
import { showToastError } from '@/shared/utils/toast';

export const SewBillingCycleRangeExport: React.FC<
  TSewBillingCycleRangeExportProps
> = ({
  icon,
  title,
  description,
  loaderText,
  baseFileNamePrefix,
  fetchExport,
}) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const downloadExports = useDownloadSewExports();

  const defaultPeriod = getLatestExportableBillingCycle().toISOString();

  const onSubmit = async (
    data: TSubmittedData<TSewBillingCycleRangeExportInputSchema>,
    handleClose: () => void,
  ) => {
    const { startBillingCycleTs, endBillingCycleTs } = data.data;
    const formattedStart = formatLocalTime(
      startBillingCycleTs,
      'UTC',
      'MMMM_yyyy',
    ).toLowerCase();
    const formattedEnd = formatLocalTime(
      endBillingCycleTs,
      'UTC',
      'MMMM_yyyy',
    ).toLowerCase();
    const baseFileName = `${baseFileNamePrefix}_${formattedStart}_${formattedEnd}`;

    try {
      const {
        url: exportUrl,
        log: logFile,
        hasErrors,
      } = await fetchExport(
        isoDateToBillingTs(startBillingCycleTs),
        isoDateToBillingTs(endBillingCycleTs),
      );
      downloadExports({ exportUrl, logFile, baseFileName, hasErrors });
      handleClose();
    } catch {
      showToastError(t('billing:error_generating_export'));
    }
  };

  return (
    <ModalController
      trigger={
        <ButtonWrapper>
          <FeatureCard icon={icon} title={title} description={description} />
        </ButtonWrapper>
      }
    >
      {(handleClose) => (
        <ModalContainer gap="2rem" width="30rem">
          <Typography variant="h6">{title}</Typography>
          <Form<TSewBillingCycleRangeExportInputSchema>
            onSubmit={(data) => onSubmit(data, handleClose)}
            schema={sewBillingCycleRangeExportValidationInputSchema}
            loaderText={loaderText}
            defaultValues={{
              startBillingCycleTs: defaultPeriod,
              endBillingCycleTs: defaultPeriod,
            }}
          >
            {() => <SewBillingCycleRangeExportForm handleClose={handleClose} />}
          </Form>
        </ModalContainer>
      )}
    </ModalController>
  );
};
